import { api } from "./api";
import { clearRegistrationUser } from "utils/fns";
import {
  RegistrarElogioPayload,
  RegistrarFavoritoPayload,
  RegistrarCompartilhamentoPayload
} from "types/iniciativa-action";

const dispatchActionLogCompletedEvent = (actionId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('action-log:completed', {
      detail: {
        actionId,
        timestamp: Date.now()
      }
    })
  );
};

/**
 * Action IDs do Funifier para cada tipo de ação
 *
 * Estes IDs devem estar configurados no painel do Funifier com:
 * - Pontos a serem creditados
 * - Cooldowns (se aplicável)
 * - Vinculação a desafios e conquistas
 */
const ACTION_IDS = {
  ELOGIAR: 'elogiar',
  FAVORITAR: 'favoritar',
  COMPARTILHAR: 'compartilhar',
  LOGIN: 'login',
  ONBOARDING: 'onboarding',
  TIMELINE: 'timeline',
  INICIATIVAS: 'iniciativas',
  ENVIAR_VIDEO: 'enviar_video',
  CONFIRMAR: 'confirmar',
  ACOMPANHAR: 'acompanhar',
  VOTAR: 'votar'
} as const;

const registrarActionLogSemAtributos = async (
  playerId: string,
  actionId: string
): Promise<any> => {
  try {
    const playerCleaned = clearRegistrationUser(playerId);

    const actionLogPayload = {
      player: playerCleaned,
      actionId,
      value: 1,
      attributes: {}
    };

    const { data } = await api.post("/action/log", actionLogPayload);
    dispatchActionLogCompletedEvent(actionId);
    return data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Registra um elogio em uma iniciativa usando o sistema de action_log do Funifier
 */
export const registrarElogio = async (
  playerId: string,
  payload: RegistrarElogioPayload
): Promise<any> => {
  try {
    const playerCleaned = clearRegistrationUser(playerId);

    const actionLogPayload = {
      player: playerCleaned,
      actionId: ACTION_IDS.ELOGIAR,
      value: 1,
      attributes: {
        id_iniciativa: payload.id_iniciativa,
        elogio: payload.elogio
      }
    };

    const { data } = await api.post("/action/log", actionLogPayload);
    dispatchActionLogCompletedEvent(ACTION_IDS.ELOGIAR);
    return data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Registra ou remove um favorito de uma iniciativa usando o sistema de action_log do Funifier
 */
export const registrarFavorito = async (
  playerId: string,
  payload: RegistrarFavoritoPayload
): Promise<any> => {
  try {
    const playerCleaned = clearRegistrationUser(playerId);

    const actionLogPayload = {
      player: playerCleaned,
      actionId: ACTION_IDS.FAVORITAR,
      value: 1,
      attributes: {
        id_iniciativa: payload.id_iniciativa,
        ativo: payload.ativo
      }
    };

    const { data } = await api.post("/action/log", actionLogPayload);
    dispatchActionLogCompletedEvent(ACTION_IDS.FAVORITAR);
    return data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Registra um compartilhamento de uma iniciativa usando o sistema de action_log do Funifier
 */
export const registrarCompartilhamento = async (
  playerId: string,
  payload: RegistrarCompartilhamentoPayload
): Promise<any> => {
  try {
    const playerCleaned = clearRegistrationUser(playerId);

    const actionLogPayload = {
      player: playerCleaned,
      actionId: ACTION_IDS.COMPARTILHAR,
      value: 1,
      attributes: {
        id_iniciativa: payload.id_iniciativa
      }
    };

    const { data } = await api.post("/action/log", actionLogPayload);
    dispatchActionLogCompletedEvent(ACTION_IDS.COMPARTILHAR);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const registrarOnboarding = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.ONBOARDING);
};

export const registrarTimeline = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.TIMELINE);
};

export const registrarIniciativas = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.INICIATIVAS);
};

export const registrarLogin = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.LOGIN);
};

export const registrarEnviarVideo = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.ENVIAR_VIDEO);
};

export const registrarConfirmar = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.CONFIRMAR);
};

export const registrarAcompanhar = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.ACOMPANHAR);
};

export const registrarVotar = async (playerId: string): Promise<any> => {
  return registrarActionLogSemAtributos(playerId, ACTION_IDS.VOTAR);
};
